import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUnifiedApi, useRoutePaths } from '../../shared/useUnifiedApi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea
} from 'recharts';
import {
  Truck,
  Container,
  Plus,
  RefreshCw,
  ArrowLeft,
  Search,
  Edit,
  Trash2,
  X,
  Download,
  BarChart3,
  AlertTriangle,
  Wrench,
  ChevronDown,
  ChevronUp,
  Users,
  Route,
  ClipboardCheck,
  XCircle,
  Clock,
  CheckCircle,
  Gauge,
  Bell,
  Printer
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { SoldVehicleButton } from '../components/SoldVehicleButton';

const VEHICLE_STATUSES = {
  active: { label: 'Actif', color: 'bg-green-500/20 text-green-400' },
  maintenance: { label: 'En entretien', color: 'bg-amber-500/20 text-amber-400' },
  inactive: { label: 'Inactif', color: 'bg-slate-500/20 text-slate-400' }
};

const FleetPage = ({ token, mode = 'admin', atelierOrgId = null, workspaceOrgId = null }) => {
  const { get, post, put, del } = useUnifiedApi(token, mode, atelierOrgId);
  const routes = useRoutePaths(mode);
  const isAtelier = mode === 'atelier';
  const isAtelierFleet = !!atelierOrgId;
  const isMechanic = mode === 'mechanic';
  const navigate = useNavigate();

  // Build workspace query param for mechanic mode
  const wsParam = isMechanic && workspaceOrgId ? `?workspace_org_id=${workspaceOrgId}` : '';
  const [activeTab, setActiveTab] = useState('trucks');
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [saaqAlerts, setSaaqAlerts] = useState({});
  const [measurementStatus, setMeasurementStatus] = useState({});
  const [measurementHistory, setMeasurementHistory] = useState({});
  const [wearStatus, setWearStatus] = useState({});

  const downloadPdf = async (url, fallbackName) => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const tkn = localStorage.getItem(mode === 'mechanic' ? 'mechanic_token' : 'admin_token');
    try {
      const resp = await fetch(`${apiUrl}/api${url}`, { headers: { 'Authorization': `Bearer ${tkn}` } });
      if (!resp.ok) { toast.error('PDF non disponible'); return; }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fallbackName;
      const disp = resp.headers.get('Content-Disposition');
      if (disp) { const m = disp.match(/filename="(.+)"/); if (m) a.download = m[1]; }
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur téléchargement PDF'); }
  };

  const printLastInspection = async (vehicleId, unitNumber) => {
    // Navigate to inspection page in classic mode with auto-print
    navigate(`${routes.inspection(vehicleId)}?autoprint=classic`);
  };

  const printLastMeasurement = async (vehicleId, unitNumber) => {
    // Navigate to measurements page in classic mode with auto-print
    navigate(`${routes.measurements(vehicleId)}?autoprint=classic`);
  };

  // Kept for potential backend PDF fallback (not currently used)
  // eslint-disable-next-line no-unused-vars
  const _printLastInspectionBackendPdf = async (vehicleId, unitNumber) => {
    const res = await get(`/saas/saaq/inspections?vehicle_id=${vehicleId}`);
    if (res.success && res.data?.length > 0) {
      const last = res.data[0];
      downloadPdf(`/saas/saaq/inspections/${last.id}/pdf`, `Inspection_${unitNumber}.pdf`);
    } else {
      toast.error('Aucune inspection trouvée pour ce véhicule');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _printLastMeasurementBackendPdf = async (vehicleId, unitNumber) => {
    const res = await get(`/fleet/vehicles/${vehicleId}/measurements`);
    if (res.success && res.data?.length > 0) {
      const last = res.data[0];
      downloadPdf(`/fleet/measurements/${last.id}/pdf`, `Mesures_${unitNumber}.pdf`);
    } else {
      toast.error('Aucune mesure trouvée pour ce véhicule');
    }
  };

  const [form, setForm] = useState({
    vehicle_type: 'truck',
    unit_number: '',
    plate_number: '',
    brand: '',
    model: '',
    year: '',
    vin: '',
    current_odometer: 0,
    pnbv: '',
    notes: '',
    is_subcontractor: false,
    subcontractor_name: ''
  });

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const [vehiclesRes, statsRes, alertsRes, measureRes, wearRes] = await Promise.all([
      get(`/fleet/vehicles${wsParam}`),
      get(`/fleet/stats${wsParam}`),
      get(`/saaq/alerts${wsParam}`),
      get(`/fleet/measurement-status${wsParam}`),
      get(`/maintenance/fleet-status${wsParam}`)
    ]);
    if (vehiclesRes.success) setVehicles(vehiclesRes.data);
    if (statsRes.success) setStats(statsRes.data);
    if (alertsRes.success) {
      const alertMap = {};
      (alertsRes.data || []).forEach(a => { alertMap[a.vehicle_id] = a; });
      setSaaqAlerts(alertMap);
    }
    if (measureRes.success) setMeasurementStatus(measureRes.data?.statuses || {});
    if (wearRes.success) setWearStatus(wearRes.data || {});
    setLoading(false);
  }, [get, wsParam]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const fetchUsage = async (vehicleId) => {
    if (usageData[vehicleId]) {
      setExpandedVehicle(expandedVehicle === vehicleId ? null : vehicleId);
      return;
    }
    const [usageRes, historyRes] = await Promise.all([
      get(`/fleet/vehicles/${vehicleId}/usage`),
      get(`/fleet/vehicles/${vehicleId}/measurement-history`)
    ]);
    if (usageRes.success) {
      setUsageData(prev => ({ ...prev, [vehicleId]: usageRes.data }));
    }
    if (historyRes.success) {
      setMeasurementHistory(prev => ({ ...prev, [vehicleId]: historyRes.data }));
    }
    setExpandedVehicle(vehicleId);
  };

  const resetForm = (type = 'truck') => {
    setForm({
      vehicle_type: type,
      unit_number: '', plate_number: '', brand: '', model: '',
      year: '', vin: '', current_odometer: 0, pnbv: '', notes: '', is_subcontractor: false, subcontractor_name: ''
    });
    setEditingVehicle(null);
  };

  const openAddModal = () => {
    resetForm(activeTab === 'trucks' ? 'truck' : 'trailer');
    setShowAddModal(true);
  };

  const openEditModal = (vehicle) => {
    setForm({
      vehicle_type: vehicle.vehicle_type,
      unit_number: vehicle.unit_number || '',
      plate_number: vehicle.plate_number || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      vin: vehicle.vin || '',
      current_odometer: vehicle.current_odometer || 0,
      pnbv: vehicle.pnbv || '',
      notes: vehicle.notes || '',
      is_subcontractor: !!vehicle.is_subcontractor,
      subcontractor_name: vehicle.subcontractor_name || ''
    });
    setEditingVehicle(vehicle);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      current_odometer: parseInt(form.current_odometer) || 0,
      pnbv: form.pnbv ? parseInt(form.pnbv) : null
    };

    let result;
    if (editingVehicle) {
      result = await put(`/fleet/vehicles/${editingVehicle.id}`, payload);
    } else {
      result = await post('/fleet/vehicles', payload);
    }

    if (result.success) {
      toast.success(result.data.message);
      setShowAddModal(false);
      resetForm();
      fetchVehicles();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Retirer ${vehicle.unit_number} de la flotte?`)) return;
    const result = await del(`/fleet/vehicles/${vehicle.id}`);
    if (result.success) {
      toast.success(result.data.message);
      fetchVehicles();
    } else {
      toast.error(result.error);
    }
  };

  const handleImport = async () => {
    setImportLoading(true);
    const result = await post('/fleet/import-from-drivers', {});
    if (result.success) {
      toast.success(result.data.message);
      fetchVehicles();
    } else {
      toast.error(result.error);
    }
    setImportLoading(false);
  };

  const handleCheckAlerts = async () => {
    const result = await post('/fleet/check-alerts', {});
    if (result.success) {
      const d = result.data;
      if (d.alerts_triggered === 0) {
        toast.success('Aucun vehicule en zone critique ou attention');
      } else {
        toast.warning(`${d.alerts_triggered} alerte(s) envoyee(s) par email`);
      }
    } else {
      toast.error(result.error);
    }
  };

  const trucks = vehicles.filter(v => v.vehicle_type === 'truck');
  const trailers = vehicles.filter(v => v.vehicle_type === 'trailer');
  const currentList = activeTab === 'trucks' ? trucks : trailers;

  const filtered = currentList.filter(v => {
    const text = `${v.unit_number} ${v.plate_number} ${v.brand} ${v.model}`.toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  const VehicleCard = ({ vehicle }) => {
    const isExpanded = expandedVehicle === vehicle.id;
    const usage = usageData[vehicle.id];
    const statusInfo = VEHICLE_STATUSES[vehicle.status] || VEHICLE_STATUSES.active;
    const alert = saaqAlerts[vehicle.id];
    const mStatus = measurementStatus[vehicle.id];
    const mHistory = measurementHistory[vehicle.id];
    const wear = wearStatus[vehicle.id];

    const statusLabels = { good: 'OK', warning: 'Attention', critical: 'Critique', none: 'N/A' };
    const statusColors = {
      good: 'text-green-400',
      warning: 'text-amber-400',
      critical: 'text-red-400',
      none: 'text-slate-500',
    };
    const dotColors = {
      good: 'bg-green-500',
      warning: 'bg-amber-500',
      critical: 'bg-red-500',
      none: 'bg-slate-600',
    };
    const unitLabel = (u) => ({ mm: 'mm', pouces: 'po', '32emes': '/32', pourcent: '%' }[u] || u);

    return (
      <div className={`bg-slate-900 border rounded-xl overflow-hidden transition-all hover:border-slate-700 ${
        alert?.severity === 'overdue' ? 'border-red-500/40' : alert?.severity === 'urgent' ? 'border-amber-500/40' : 'border-slate-800'
      }`} data-testid={`vehicle-card-${vehicle.id}`}>
        {/* Alert banner */}
        {alert && (
          <div className={`px-4 py-2 flex items-center gap-2 text-xs ${
            alert.severity === 'overdue' ? 'bg-red-500/10 text-red-400' :
            alert.severity === 'urgent' ? 'bg-amber-500/10 text-amber-400' :
            'bg-blue-500/10 text-blue-400'
          }`} data-testid={`alert-banner-${vehicle.id}`}>
            {alert.severity === 'overdue' ? <XCircle size={14} /> : alert.severity === 'urgent' ? <AlertTriangle size={14} /> : <Clock size={14} />}
            <span className="font-medium">{alert.message}</span>
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                {vehicle.vehicle_type === 'truck'
                  ? <Truck size={22} className="text-amber-500" />
                  : <Container size={22} className="text-blue-400" />}
              </div>
              <div>
                <p className="text-white font-bold text-lg">{vehicle.unit_number}</p>
                {vehicle.plate_number && (
                  <p className="text-slate-400 text-sm">{vehicle.plate_number}</p>
                )}
                {vehicle.is_subcontractor && (
                  <p className="text-xs text-amber-400 mt-0.5" data-testid={`subcontractor-badge-${vehicle.id}`}>Sous-traitant · {vehicle.subcontractor_name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
              <button
                onClick={() => openEditModal(vehicle)}
                className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-amber-500 transition-colors"
                data-testid={`edit-vehicle-${vehicle.id}`}
              >
                <Edit size={16} />
              </button>
              {!isMechanic && <SoldVehicleButton vehicle={vehicle} put={put} onDone={fetchVehicles} />}
              <button
                onClick={() => handleDelete(vehicle)}
                className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400 transition-colors"
                data-testid={`delete-vehicle-${vehicle.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {/* Vehicle details */}
          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
            {vehicle.brand && (
              <div className="text-slate-400">
                <span className="text-slate-500">Marque: </span>{vehicle.brand}
              </div>
            )}
            {vehicle.model && (
              <div className="text-slate-400">
                <span className="text-slate-500">Modèle: </span>{vehicle.model}
              </div>
            )}
            {vehicle.year && (
              <div className="text-slate-400">
                <span className="text-slate-500">Année: </span>{vehicle.year}
              </div>
            )}
            {vehicle.current_odometer > 0 && (
              <div className="text-slate-400">
                <span className="text-slate-500">Odomètre: </span>{vehicle.current_odometer.toLocaleString()} km
              </div>
            )}
            {vehicle.pnbv && (
              <div className="text-slate-400">
                <span className="text-slate-500">PNBV: </span>{vehicle.pnbv.toLocaleString()} kg
              </div>
            )}
            {vehicle.vin && (
              <div className="text-slate-400 col-span-2">
                <span className="text-slate-500">VIN: </span>
                <span className="font-mono text-xs">{vehicle.vin}</span>
              </div>
            )}
          </div>

          {/* Measurement health indicator */}
          {mStatus && mStatus.status !== 'none' && (
            <div className={`flex items-center gap-3 px-3 py-2 rounded-lg mb-3 ${
              mStatus.status === 'critical' ? 'bg-red-500/10 border border-red-500/20' :
              mStatus.status === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
              'bg-green-500/10 border border-green-500/20'
            }`} data-testid={`measurement-badge-${vehicle.id}`}>
              <Gauge size={14} className={statusColors[mStatus.status]} />
              <div className="flex items-center gap-3 text-xs flex-1">
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${dotColors[mStatus.brake_status]}`} />
                  <span className="text-slate-500">Freins:</span>
                  <span className={`font-semibold ${statusColors[mStatus.brake_status]}`}>
                    {statusLabels[mStatus.brake_status]}
                    {mStatus.min_brake != null && <span className="text-slate-500 font-normal ml-1">({mStatus.min_brake}{unitLabel(mStatus.brake_unit)})</span>}
                  </span>
                </span>
                <span className="text-slate-700">|</span>
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${dotColors[mStatus.tire_status]}`} />
                  <span className="text-slate-500">Pneus:</span>
                  <span className={`font-semibold ${statusColors[mStatus.tire_status]}`}>
                    {statusLabels[mStatus.tire_status]}
                    {mStatus.min_tire != null && <span className="text-slate-500 font-normal ml-1">({mStatus.min_tire}{unitLabel(mStatus.tire_unit)})</span>}
                  </span>
                </span>
              </div>
              {mStatus.last_date && (
                <span className="text-[10px] text-slate-600">{mStatus.last_date.slice(0, 10)}</span>
              )}
            </div>
          )}
          {mStatus && mStatus.status === 'none' && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg mb-3 bg-slate-800/50 border border-slate-700/30" data-testid={`measurement-badge-${vehicle.id}`}>
              <Gauge size={12} className="text-slate-600" />
              <span className="text-[11px] text-slate-600">Aucune mesure freins/pneus enregistrée</span>
            </div>
          )}

          {/* Maintenance Wear Indicators */}
          {wear && wear.length > 0 && (() => {
            const withLife = wear.map(w => ({ ...w, life_pct: Math.max(0, 100 - w.wear_pct) }));
            const critical = withLife.filter(w => w.life_pct < 20);
            const warning = withLife.filter(w => w.life_pct >= 20 && w.life_pct < 50);
            const neverDone = withLife.filter(w => w.due_info === 'Jamais fait');
            // Show top items (already sorted by priority from backend)
            const topItems = withLife.slice(0, 5);
            const worstLife = Math.min(...withLife.map(w => w.life_pct));
            const overallColor = worstLife < 20 ? 'red' : worstLife < 50 ? 'amber' : 'green';
            const overallBg = { red: 'bg-red-500/10 border-red-500/20', amber: 'bg-amber-500/10 border-amber-500/20', green: 'bg-green-500/10 border-green-500/20' };
            const overallText = { red: 'text-red-400', amber: 'text-amber-400', green: 'text-green-400' };
            const summaryParts = [];
            if (critical.length > 0) summaryParts.push(`${critical.length} a faire`);
            if (warning.length > 0) summaryParts.push(`${warning.length} a surveiller`);
            if (neverDone.length > 0 && summaryParts.length === 0) summaryParts.push(`${neverDone.length} jamais fait`);
            if (summaryParts.length === 0) summaryParts.push('Tout OK');
            return (
              <div
                className={`rounded-lg border p-3 mb-3 cursor-pointer hover:opacity-80 transition-opacity ${overallBg[overallColor]}`}
                data-testid={`wear-indicators-${vehicle.id}`}
                onClick={() => navigate(routes.maintenance(vehicle.id))}
                title="Cliquer pour voir/modifier les entretiens"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
                    <Wrench size={12} />
                    Entretien
                  </span>
                  <span className={`text-[10px] font-semibold ${overallText[overallColor]}`}>
                    {summaryParts.join(' · ')}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {topItems.map(item => {
                    const barColor = item.life_pct < 20 ? 'bg-red-500' : item.life_pct < 50 ? 'bg-amber-500' : 'bg-green-500';
                    return (
                      <div key={item.item_id} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 w-28 truncate flex-shrink-0" title={item.name}>
                          {item.is_km && <span className="text-amber-500 mr-0.5">●</span>}
                          {item.name}
                        </span>
                        <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${item.life_pct}%` }} />
                        </div>
                        <span className={`text-[9px] font-mono w-14 text-right flex-shrink-0 ${item.life_pct < 20 ? 'text-red-400' : item.life_pct < 50 ? 'text-amber-400' : 'text-green-400'}`}>
                          {item.life_pct > 0 ? `${item.life_pct}%` : item.due_info}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {wear.length > 5 && (
                  <p className="text-[9px] text-slate-600 mt-1.5 text-right">+{wear.length - 5} autre(s)</p>
                )}
              </div>
            );
          })()}

          {/* Action buttons */}
          <div className="grid grid-cols-4 gap-2">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => navigate(routes.inspection(vehicle.id))}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                  alert?.severity === 'overdue'
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
                }`}
                data-testid={`inspect-vehicle-${vehicle.id}`}
              >
                <ClipboardCheck size={13} />
                Inspection
              </button>
              <button
                onClick={() => printLastInspection(vehicle.id, vehicle.unit_number)}
                className="flex items-center justify-center gap-1 py-1 rounded-lg bg-slate-800 text-slate-500 hover:text-amber-400 hover:bg-slate-700 text-[10px] transition-colors"
                data-testid={`print-inspection-${vehicle.id}`}
                title="Imprimer dernière inspection"
              >
                <Printer size={10} /> PDF
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => navigate(routes.measurements(vehicle.id))}
                className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-xs font-medium transition-colors"
                data-testid={`measure-vehicle-${vehicle.id}`}
              >
                <Gauge size={13} />
                Mesures
              </button>
              <button
                onClick={() => printLastMeasurement(vehicle.id, vehicle.unit_number)}
                className="flex items-center justify-center gap-1 py-1 rounded-lg bg-slate-800 text-slate-500 hover:text-blue-400 hover:bg-slate-700 text-[10px] transition-colors"
                data-testid={`print-measurement-${vehicle.id}`}
                title="Imprimer dernières mesures"
              >
                <Printer size={10} /> PDF
              </button>
            </div>
            <button
              onClick={() => navigate(routes.workOrders(vehicle.id))}
              className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 text-xs font-medium transition-colors"
              data-testid={`workorder-vehicle-${vehicle.id}`}
            >
              <Wrench size={13} />
              Bons
            </button>
            <button
              onClick={() => fetchUsage(vehicle.id)}
              className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-xs transition-colors"
              data-testid={`usage-toggle-${vehicle.id}`}
            >
              <BarChart3 size={13} />
              Historique
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        </div>

        {/* Expanded usage panel */}
        {isExpanded && usage && (
          <div className="border-t border-slate-800 p-4 bg-slate-900/50 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <p className="text-amber-500 font-bold text-lg">{usage.total_km?.toLocaleString() || 0}</p>
                <p className="text-slate-500 text-xs">km total</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <p className="text-blue-400 font-bold text-lg">{usage.total_days_used || 0}</p>
                <p className="text-slate-500 text-xs">jours utilisés</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <p className="text-green-400 font-bold text-lg">{usage.total_drivers || 0}</p>
                <p className="text-slate-500 text-xs">chauffeurs</p>
              </div>
            </div>

            {usage.drivers_usage?.length > 0 && (
              <div>
                <p className="text-slate-400 text-xs font-medium mb-2 flex items-center gap-1">
                  <Users size={12} /> Chauffeurs ayant utilisé ce véhicule
                </p>
                <div className="space-y-1">
                  {usage.drivers_usage.map(d => (
                    <div key={d.driver_id} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2 text-sm">
                      <span className="text-white">{d.name}</span>
                      <div className="flex gap-3 text-slate-400 text-xs">
                        <span>{d.total_km?.toLocaleString()} km</span>
                        <span>{d.days_used} jour(s)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {usage.drivers_usage?.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-2">Aucune donnée d'utilisation disponible</p>
            )}

            {/* Measurement History Chart */}
            {mHistory && mHistory.length > 0 && (
              <div data-testid={`measurement-chart-${vehicle.id}`}>
                <p className="text-slate-400 text-xs font-medium mb-2 flex items-center gap-1">
                  <Gauge size={12} /> Évolution des mesures freins & pneus
                </p>
                <div className="bg-slate-800 rounded-lg p-3">
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={mHistory} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <ReferenceArea y1={0} y2={3} fill="#ef4444" fillOpacity={0.08} />
                      <ReferenceArea y1={3} y2={5} fill="#f59e0b" fillOpacity={0.08} />
                      <ReferenceArea y1={5} y2={20} fill="#22c55e" fillOpacity={0.06} />
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                        tickFormatter={(d) => d.slice(5)}
                        stroke="#475569"
                      />
                      <YAxis
                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                        stroke="#475569"
                        domain={[0, (dataMax) => Math.min(Math.max(dataMax * 1.1, 12), 25)]}
                      />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: 12 }}
                        labelStyle={{ color: '#94a3b8' }}
                        formatter={(value, name) => {
                          const labels = { min_brake: 'Freins (min)', min_tire: 'Pneus (min)', avg_brake: 'Freins (moy)', avg_tire: 'Pneus (moy)' };
                          return [value != null ? `${value}` : '-', labels[name] || name];
                        }}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Line type="monotone" dataKey="min_brake" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} name="min_brake" connectNulls />
                      <Line type="monotone" dataKey="min_tire" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} name="min_tire" connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="flex items-center justify-center gap-6 mt-2">
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="w-3 h-0.5 bg-amber-500 rounded" /> <span className="text-slate-400">Freins (min)</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="w-3 h-0.5 bg-blue-500 rounded" /> <span className="text-slate-400">Pneus (min)</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded bg-red-500/30 border border-red-500/50" /> <span className="text-slate-500">Critique</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded bg-amber-500/30 border border-amber-500/50" /> <span className="text-slate-500">Attention</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded bg-green-500/30 border border-green-500/50" /> <span className="text-slate-500">Bon</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
            {mHistory && mHistory.length === 0 && (
              <p className="text-slate-600 text-xs text-center py-2">Aucun historique de mesures disponible</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back */}
      {!isAtelier && (
        <Link to={mode === 'mechanic' ? '/admin/mecanicien' : '/admin/dashboard'} className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors">
          <ArrowLeft size={18} />
          <span>Retour au tableau de bord</span>
        </Link>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="fleet-page-title">
            <Truck className="text-amber-500" />
            Flotte de véhicules
          </h1>
          <p className="text-slate-400">
            {stats ? `${stats.total_trucks} camion(s) · ${stats.total_trailers} remorque(s)` : 'Chargement...'}
            {stats?.in_maintenance > 0 && ` · ${stats.in_maintenance} en entretien`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleImport}
            disabled={importLoading}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="import-from-drivers-btn"
          >
            {importLoading ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
            Importer des chauffeurs
          </Button>
          <Button
            onClick={handleCheckAlerts}
            variant="outline"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
            data-testid="check-alerts-btn"
          >
            <Bell size={16} className="mr-2" />
            Verifier alertes
          </Button>
          <Button
            onClick={() => navigate(routes.connections)}
            variant="outline"
            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300"
            data-testid="connections-btn"
          >
            <Users size={16} className="mr-2" />
            Clients
          </Button>
          <Button
            onClick={openAddModal}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="add-vehicle-btn"
          >
            <Plus size={18} className="mr-2" />
            Ajouter un véhicule
          </Button>
          <button
            onClick={fetchVehicles}
            className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
        <button
          onClick={() => setActiveTab('trucks')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'trucks'
              ? 'bg-amber-500 text-black'
              : 'text-slate-400 hover:text-white hover:bg-slate-800'
          }`}
          data-testid="tab-trucks"
        >
          <Truck size={16} />
          Camions ({trucks.length})
        </button>
        <button
          onClick={() => setActiveTab('trailers')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'trailers'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-800'
          }`}
          data-testid="tab-trailers"
        >
          <Container size={16} />
          Remorques ({trailers.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher par numéro, plaque, marque..."
          className="w-full bg-slate-900 border border-slate-800 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-amber-500"
          data-testid="search-vehicles"
        />
      </div>

      {/* Vehicle list */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-slate-900 rounded-xl h-48 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-slate-900 rounded-xl border border-slate-800">
          {activeTab === 'trucks'
            ? <Truck className="text-slate-600 mx-auto mb-4" size={48} />
            : <Container className="text-slate-600 mx-auto mb-4" size={48} />}
          <p className="text-slate-400 font-medium">
            {searchTerm ? 'Aucun véhicule trouvé' : `Aucun ${activeTab === 'trucks' ? 'camion' : 'remorque'} dans la flotte`}
          </p>
          <p className="text-slate-500 text-sm mt-1">
            {!searchTerm && "Ajoutez un véhicule manuellement ou importez depuis les profils chauffeurs"}
          </p>
          {!searchTerm && (
            <div className="flex gap-3 justify-center mt-4">
              <Button onClick={handleImport} variant="outline" className="border-slate-700 text-slate-300">
                <Download size={16} className="mr-2" /> Importer
              </Button>
              <Button onClick={openAddModal} className="bg-amber-500 hover:bg-amber-600 text-black">
                <Plus size={16} className="mr-2" /> Ajouter
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                {form.vehicle_type === 'truck'
                  ? <Truck className="text-amber-500" size={20} />
                  : <Container className="text-blue-400" size={20} />}
                {editingVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
              </h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Type selector */}
              {!editingVehicle && (
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Type de véhicule</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, vehicle_type: 'truck' }))}
                      className={`flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors ${
                        form.vehicle_type === 'truck'
                          ? 'border-amber-500 bg-amber-500/10 text-amber-500'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="type-truck-btn"
                    >
                      <Truck size={18} /> Camion
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, vehicle_type: 'trailer' }))}
                      className={`flex items-center justify-center gap-2 py-3 rounded-lg border transition-colors ${
                        form.vehicle_type === 'trailer'
                          ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="type-trailer-btn"
                    >
                      <Container size={18} /> Remorque
                    </button>
                  </div>
                </div>
              )}

              {/* Unit number */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Numéro d'unité *</label>
                <Input
                  value={form.unit_number}
                  onChange={e => setForm(f => ({ ...f, unit_number: e.target.value }))}
                  placeholder="Ex: TRK-001"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                  data-testid="input-unit-number"
                />
              </div>

              {/* Plate + VIN */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Plaque</label>
                  <Input
                    value={form.plate_number}
                    onChange={e => setForm(f => ({ ...f, plate_number: e.target.value }))}
                    placeholder="ABC 1234"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-plate"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">VIN</label>
                  <Input
                    value={form.vin}
                    onChange={e => setForm(f => ({ ...f, vin: e.target.value }))}
                    placeholder="1HGBH41..."
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                    data-testid="input-vin"
                  />
                </div>
              </div>

              {/* Brand + Model */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Marque</label>
                  <Input
                    value={form.brand}
                    onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                    placeholder="Ex: Peterbilt"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-brand"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Modèle</label>
                  <Input
                    value={form.model}
                    onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                    placeholder="Ex: 579"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-model"
                  />
                </div>
              </div>

              {/* Year + Odometer + PNBV */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Année</label>
                  <Input
                    value={form.year}
                    onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                    placeholder="2024"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-year"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Odomètre (km)</label>
                  <Input
                    type="number"
                    value={form.current_odometer}
                    onChange={e => setForm(f => ({ ...f, current_odometer: e.target.value }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-odometer"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">PNBV (kg)</label>
                  <Input
                    type="number"
                    value={form.pnbv}
                    onChange={e => setForm(f => ({ ...f, pnbv: e.target.value }))}
                    placeholder="4500"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="input-pnbv"
                  />
                </div>
              </div>

              {/* PNBV info */}
              <div className="bg-slate-800/50 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-400">
                  Le PNBV (Poids Nominal Brut du Véhicule) détermine les exigences d'entretien.
                  Véhicules de 4 500 kg+ : entretien obligatoire aux 6 mois.
                  Véhicules de 7 258 kg+ : mesures de freins obligatoires.
                </p>
              </div>

              {!isMechanic && (
                <div className="rounded-lg border border-slate-700 p-3 space-y-2" data-testid="subcontractor-block">
                  <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                    <input type="checkbox" checked={form.is_subcontractor} onChange={e => setForm(f => ({ ...f, is_subcontractor: e.target.checked }))}
                      className="accent-amber-500 w-4 h-4" data-testid="input-is-subcontractor" />
                    Appartient à un sous-traitant
                  </label>
                  {form.is_subcontractor && (
                    <input value={form.subcontractor_name} onChange={e => setForm(f => ({ ...f, subcontractor_name: e.target.value }))} required
                      placeholder="Nom du sous-traitant (ex. Transport TSC)" list="subcontractor-names"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white text-sm focus:outline-none focus:border-amber-500" data-testid="input-subcontractor-name" />
                  )}
                  <datalist id="subcontractor-names">
                    {[...new Set(vehicles.filter(v => v.is_subcontractor && v.subcontractor_name).map(v => v.subcontractor_name))].map(n => <option key={n} value={n} />)}
                  </datalist>
                  <p className="text-xs text-slate-500">Suivi GPS et réparations identiques ; compté à part dans Analytics, le résumé mensuel et son propre dossier SAAQ.</p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Notes supplémentaires..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-amber-500 h-20 resize-none"
                  data-testid="input-notes"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="flex-1 border-slate-700"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="submit-vehicle-btn"
                >
                  {editingVehicle ? 'Mettre à jour' : 'Ajouter'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetPage;
